import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Form, Spinner, Stack } from 'react-bootstrap';
import AdminSidebar from '../../components/AdminSidebar';
import {
  AdminNotificationsProvider,
  useAdminNotifications,
} from '../../components/AdminNotifications';
import useAdminGuard from '../../hooks/useAdminGuard';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import { hasPermission, PERMISSION_IDS } from '../../utils/permissions';
import '../../styles/admin.css';
import './AdminNewsletterDetallePage.css';
import { logError } from '../../utils/logger';

const resolveImageUrl = (source) => {
  if (!source) return null;
  const sanitized = String(source).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(sanitized)) return sanitized;
  const base = getApiBaseUrl();
  if (sanitized.startsWith('/')) return `${base}${sanitized}`;
  return `${base}/uploads/newsletters/images/${sanitized}`;
};

// Sanitizar HTML
const sanitizeHtml = (html) => {
  if (!html) return '';
  const allowedTags = new Set(['P', 'B', 'STRONG', 'I', 'EM', 'U', 'UL', 'OL', 'LI', 'H1', 'H2', 'H3', 'BR', 'A', 'IMG', 'DIV', 'SECTION']);
  const allowedAttrs = {
    'A': ['href', 'target', 'rel', 'class', 'aria-label'],
    'IMG': ['src', 'alt']
  };
  const allowedButtonClasses = new Set(['nld-btn', 'nld-btn-primary', 'nld-btn-secondary', 'nld-btn-outline', 'nld-btn-lg', 'nld-btn-sm', 'nld-btn-block']);
  
  const div = document.createElement('div');
  div.innerHTML = String(html || '');
  
  const walker = (node) => {
    Array.from(node.children).forEach(child => {
      if (!allowedTags.has(child.tagName)) {
        const frag = document.createDocumentFragment();
        while (child.firstChild) frag.appendChild(child.firstChild);
        child.replaceWith(frag);
      } else {
        Array.from(child.attributes).forEach(attr => {
          const tag = child.tagName;
          const attrName = attr.name.toLowerCase();
          if (!((allowedAttrs[tag] || []).includes(attrName))) {
            child.removeAttribute(attr.name);
          }
        });
        if (child.tagName === 'A') {
          const href = child.getAttribute('href') || '';
          if (!/^https?:\/\//i.test(href)) child.removeAttribute('href');
          child.setAttribute('target', '_blank');
          child.setAttribute('rel', 'noopener noreferrer');
          const cls = (child.getAttribute('class') || '').split(/\s+/).filter(c => allowedButtonClasses.has(c));
          if (cls.length) child.setAttribute('class', cls.join(' '));
          else child.removeAttribute('class');
        }
        if (child.tagName === 'IMG') {
          const src = child.getAttribute('src') || '';
          if (!/^https?:\/\//i.test(src) && !/^data:/i.test(src)) child.remove();
        }
        walker(child);
      }
    });
  };
  walker(div);
  return div.innerHTML;
};

// Sanitizar input de texto
const sanitizeInput = (value, { maxLen = 500, pattern = /^[\w\sÁÉÍÓÚáéíóúÑñüÜ.,:;()\-\[\]!¡¿?]+$/ } = {}) => {
  let v = String(value || '').trim();
  if (v.length > maxLen) v = v.substring(0, maxLen);
  if (!pattern.test(v)) throw new Error('Entrada contiene caracteres no permitidos');
  return v;
};

const debounce = (fn, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), wait);
  };
};

const AdminNewsletterDetalleInner = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAdminGuard();
  const { notify } = useAdminNotifications();

  const hasEditarNewsletter = useMemo(() => hasPermission(user, PERMISSION_IDS.EDITAR_NEWSLETTER), [user]);

  const [newsletter, setNewsletter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sections, setSections] = useState([]);
  const [title, setTitle] = useState('');
  const [titleEditing, setTitleEditing] = useState(false);
  const [shortDescription, setShortDescription] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const coverInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const previewIframeRef = useRef(null);
  const lastPreviewHtmlRef = useRef('');
  const editorRefs = useRef({});
  const isUserEditingRef = useRef({});

  // Verificar acceso
  useEffect(() => {
    if (user && !hasEditarNewsletter) {
      notify({
        title: 'Acceso denegado',
        message: 'No tienes permiso para editar newsletters',
        variant: 'error',
      });
      navigate('/403', { replace: true });
    }
  }, [user, hasEditarNewsletter, navigate, notify]);

  const fetchNewsletter = useCallback(async () => {
    if (!token || !id) return;
    setLoading(true);
    try {
      const response = await fetch(buildApiUrl(`/api/newsletters/${id}/admin`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error(`No se pudo cargar el newsletter (${response.status})`);
      const data = await response.json();
      setNewsletter(data);
      setTitle(data.title || '');
      setShortDescription(data.shortDescription || '');
      setCoverImage(data.coverImage || null);

      // Parsear contenido HTML en secciones
      if (data.contentHtml) {
        const tmp = document.createElement('div');
        tmp.innerHTML = data.contentHtml;
        const blocks = Array.from(tmp.childNodes);
        const parsedSections = [];
        blocks.forEach(node => {
          if (node.nodeType !== 1) return;
          if (node.tagName === 'P') {
            const img = node.querySelector('img');
            if (img) {
              parsedSections.push({
                type: 'image',
                content: resolveImageUrl(img.getAttribute('src')),
                id: Date.now() + Math.random(),
              });
              return;
            }
            parsedSections.push({
              type: 'text',
              content: node.innerHTML || '',
              id: Date.now() + Math.random(),
            });
          } else if (node.tagName === 'DIV' || node.tagName === 'SECTION') {
            parsedSections.push({
              type: 'text',
              content: node.innerHTML || '',
              id: Date.now() + Math.random(),
            });
          }
        });
        setSections(parsedSections);
      } else {
        setSections([]);
      }
    } catch (error) {
      notify({
        title: 'Error al cargar el newsletter',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [id, notify, token]);

  useEffect(() => {
    if (!token || !id) return;
    fetchNewsletter();
  }, [token, id, fetchNewsletter]);

  const collectContent = useCallback(() => {
    const parts = [];
    sections.forEach(section => {
      if (section.type === 'image') {
        parts.push(`<p><img src="${section.content}" style="max-width:100%" alt="Imagen"/></p>`);
      } else if (section.type === 'text') {
        const clean = sanitizeHtml(section.content);
        parts.push(`<div>${clean}</div>`);
      }
    });
    return parts.join('\n');
  }, [sections]);

  const updatePreview = useCallback(() => {
    const htmlContent = collectContent();
    if (htmlContent === lastPreviewHtmlRef.current) return;
    lastPreviewHtmlRef.current = htmlContent;
    
    const iframe = previewIframeRef.current;
    if (!iframe) {
      // Si el iframe no existe aún, intentar de nuevo en el siguiente frame
      setTimeout(updatePreview, 100);
      return;
    }
    
    // Inicializar iframe si no tiene contenido
    if (!iframe.srcdoc && !iframe.contentDocument) {
      const skeleton = `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><style>body{font-family:'Open Sans',system-ui,Segoe UI,Roboto,Arial;padding:1rem;line-height:1.6;margin:0} img{border-radius:8px;max-width:100%;height:auto;display:block;margin:1rem 0} .nld-btn{display:inline-block;padding:10px 14px;border-radius:8px;font-weight:600;text-decoration:none;margin:0.5rem 0} .nld-btn-primary{background:#1e40af;color:#fff} .nld-btn-secondary{background:#334155;color:#fff} .nld-btn-outline{background:transparent;color:#1e40af;border:2px solid #1e40af} .nld-btn-lg{padding:12px 18px;font-size:1.05rem} .nld-btn-sm{padding:8px 10px;font-size:.9rem} .nld-btn-block{display:block;width:100%;text-align:center} p{margin:1rem 0} div{margin:0.5rem 0}</style></head><body><div id="content"></div></body></html>`;
      iframe.srcdoc = skeleton;
    }
    
    // Esperar a que el iframe esté listo
    const tryUpdate = () => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc) {
          setTimeout(tryUpdate, 50);
          return;
        }
        
        const container = doc.getElementById('content');
        if (container) {
          container.innerHTML = htmlContent;
        } else {
          // Si no existe el contenedor, inicializar de nuevo
          const skeleton = `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><style>body{font-family:'Open Sans',system-ui,Segoe UI,Roboto,Arial;padding:1rem;line-height:1.6;margin:0} img{border-radius:8px;max-width:100%;height:auto;display:block;margin:1rem 0} .nld-btn{display:inline-block;padding:10px 14px;border-radius:8px;font-weight:600;text-decoration:none;margin:0.5rem 0} .nld-btn-primary{background:#1e40af;color:#fff} .nld-btn-secondary{background:#334155;color:#fff} .nld-btn-outline{background:transparent;color:#1e40af;border:2px solid #1e40af} .nld-btn-lg{padding:12px 18px;font-size:1.05rem} .nld-btn-sm{padding:8px 10px;font-size:.9rem} .nld-btn-block{display:block;width:100%;text-align:center} p{margin:1rem 0} div{margin:0.5rem 0}</style></head><body><div id="content"></div></body></html>`;
          iframe.srcdoc = skeleton;
          setTimeout(() => {
            const newDoc = iframe.contentDocument || iframe.contentWindow?.document;
            const newContainer = newDoc?.getElementById('content');
            if (newContainer) {
              newContainer.innerHTML = htmlContent;
            }
          }, 100);
        }
      } catch (error) {
        logError('[AdminNewsletterDetallePage] Error updating preview:', error);
      }
    };
    
    tryUpdate();
  }, [collectContent]);

  const updatePreviewDebounced = useMemo(() => debounce(updatePreview, 500), [updatePreview]);

  useEffect(() => {
    updatePreviewDebounced();
  }, [sections, updatePreviewDebounced]);

  // Inicializar iframe al montar
  useEffect(() => {
    const iframe = previewIframeRef.current;
    if (iframe) {
      const skeleton = `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><style>body{font-family:'Open Sans',system-ui,Segoe UI,Roboto,Arial;padding:1rem;line-height:1.6;margin:0} img{border-radius:8px;max-width:100%;height:auto;display:block;margin:1rem 0} .nld-btn{display:inline-block;padding:10px 14px;border-radius:8px;font-weight:600;text-decoration:none;margin:0.5rem 0} .nld-btn-primary{background:#1e40af;color:#fff} .nld-btn-secondary{background:#334155;color:#fff} .nld-btn-outline{background:transparent;color:#1e40af;border:2px solid #1e40af} .nld-btn-lg{padding:12px 18px;font-size:1.05rem} .nld-btn-sm{padding:8px 10px;font-size:.9rem} .nld-btn-block{display:block;width:100%;text-align:center} p{margin:1rem 0} div{margin:0.5rem 0}</style></head><body><div id="content"></div></body></html>`;
      iframe.srcdoc = skeleton;
      
      // Esperar a que el iframe cargue y actualizar
      iframe.onload = () => {
        setTimeout(() => {
          updatePreview();
        }, 100);
      };
    }
  }, [updatePreview]);


  const handleSaveTitle = async () => {
    if (!title.trim()) return;
    try {
      const cleanTitle = sanitizeInput(title, { maxLen: 120 });
      const response = await fetch(buildApiUrl(`/api/newsletters/${id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ title: cleanTitle }),
      });
      if (!response.ok) throw new Error('Error al guardar título');
      setTitleEditing(false);
      notify({
        title: 'Título guardado',
        message: 'El título se actualizó correctamente',
        variant: 'success',
      });
    } catch (error) {
      notify({
        title: 'Error',
        message: error.message || 'No se pudo guardar el título',
        variant: 'error',
      });
    }
  };

  const handleSaveDescription = async () => {
    try {
      const clean = sanitizeInput(shortDescription, { maxLen: 250 });
      const response = await fetch(buildApiUrl(`/api/newsletters/${id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ shortDescription: clean }),
      });
      if (!response.ok) throw new Error('Error al guardar descripción');
      notify({
        title: 'Descripción guardada',
        message: 'La descripción se actualizó correctamente',
        variant: 'success',
      });
    } catch (error) {
      notify({
        title: 'Error',
        message: 'No se pudo guardar la descripción',
        variant: 'error',
      });
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await fetch(buildApiUrl(`/api/newsletters/${id}/cover`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      if (!response.ok) throw new Error('Error al subir portada');
      const data = await response.json();
      setCoverImage(data.coverImage);
      notify({
        title: 'Portada actualizada',
        message: 'La portada se actualizó correctamente',
        variant: 'success',
      });
    } catch (error) {
      notify({
        title: 'Error',
        message: 'No se pudo subir la portada',
        variant: 'error',
      });
    } finally {
      setCoverUploading(false);
      if (coverInputRef.current) coverInputRef.current.value = '';
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await fetch(buildApiUrl('/api/newsletters/upload-image'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      if (!response.ok) throw new Error('Error al subir imagen');
      const data = await response.json();
      const imageUrl = resolveImageUrl(data.url);
      addImageSection(imageUrl);
      notify({
        title: 'Imagen agregada',
        message: 'La imagen se agregó correctamente',
        variant: 'success',
      });
    } catch (error) {
      notify({
        title: 'Error',
        message: 'No se pudo subir la imagen',
        variant: 'error',
      });
    } finally {
      setImageUploading(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const addTextSection = () => {
    const newId = Date.now() + Math.random();
    setSections([...sections, {
      type: 'text',
      content: '',
      id: newId,
      editing: true,
    }]);
    // Asegurar que el nuevo editor tenga dirección LTR
    setTimeout(() => {
      const editor = editorRefs.current[newId];
      if (editor) {
        editor.setAttribute('dir', 'ltr');
        editor.style.direction = 'ltr';
        editor.style.textAlign = 'left';
        editor.focus();
      }
    }, 50);
  };

  const addImageSection = (url) => {
    setSections([...sections, {
      type: 'image',
      content: url,
      id: Date.now() + Math.random(),
    }]);
  };

  const addCTASection = () => {
    const text = prompt('Texto del botón:', 'Leer más');
    if (text === null) return;
    const url = prompt('URL del botón (https://...):', 'https://');
    if (!url) return;
    const variant = prompt('Estilo (primary, secondary, outline):', 'primary');
    const cls = variant === 'secondary' ? 'nld-btn nld-btn-secondary' : 
                 variant === 'outline' ? 'nld-btn nld-btn-outline' : 
                 'nld-btn nld-btn-primary';
    const html = `<p><a href="${url.replace(/[&<>"']/g, m => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;','\'':'&#39;'}[m]))}" class="${cls}" target="_blank" rel="noopener noreferrer" aria-label="CTA">${text.replace(/[&<>"']/g, m => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;','\'':'&#39;'}[m]))}</a></p>`;
    setSections([...sections, {
      type: 'text',
      content: html,
      id: Date.now() + Math.random(),
    }]);
  };

  const handleEditorInput = (sectionId, e) => {
    const editor = editorRefs.current[sectionId];
    if (editor) {
      isUserEditingRef.current[sectionId] = true;
      
      // Forzar dirección LTR constantemente mientras se escribe
      editor.setAttribute('dir', 'ltr');
      editor.style.direction = 'ltr';
      editor.style.textAlign = 'left';
      editor.style.unicodeBidi = 'embed';
      
      // Obtener contenido actual
      const content = editor.innerHTML;
      
      // Actualizar estado
      setSections(prevSections => 
        prevSections.map(s => 
          s.id === sectionId ? { ...s, content } : s
        )
      );
      
      // Mantener el flag de edición por más tiempo para evitar interferencias
      setTimeout(() => {
        isUserEditingRef.current[sectionId] = false;
      }, 300);
    }
  };

  const handleEditorFocus = (sectionId) => {
    const editor = editorRefs.current[sectionId];
    if (editor) {
      // Asegurar dirección LTR al enfocar
      editor.setAttribute('dir', 'ltr');
      editor.style.direction = 'ltr';
      editor.style.textAlign = 'left';
      // Forzar dirección del texto
      if (editor.style.direction !== 'ltr') {
        editor.style.direction = 'ltr';
      }
    }
  };

  // Efecto para sincronizar contenido cuando cambia sections pero no está siendo editado
  useEffect(() => {
    sections.forEach(section => {
      if (section.type === 'text') {
        const editor = editorRefs.current[section.id];
        if (editor) {
          // Solo actualizar si no está siendo editado activamente
          const isEditing = isUserEditingRef.current[section.id];
          if (!isEditing && !section.editing) {
            const currentContent = editor.innerHTML.trim();
            const newContent = (section.content || '').trim();
            // Solo actualizar si el contenido es realmente diferente
            if (currentContent !== newContent) {
              editor.innerHTML = newContent || '';
              // Asegurar dirección LTR después de actualizar
              editor.setAttribute('dir', 'ltr');
              editor.style.direction = 'ltr';
              editor.style.textAlign = 'left';
              editor.style.unicodeBidi = 'embed';
            }
          }
        }
      }
    });
  }, [sections]);

  const deleteSection = (sectionId) => {
    setSections(sections.filter(s => s.id !== sectionId));
    notify({
      title: 'Sección eliminada',
      message: 'La sección se eliminó correctamente',
      variant: 'success',
    });
  };

  const moveSection = (sectionId, direction) => {
    const index = sections.findIndex(s => s.id === sectionId);
    if (index === -1) return;
    const newSections = [...sections];
    if (direction === 'up' && index > 0) {
      [newSections[index - 1], newSections[index]] = [newSections[index], newSections[index - 1]];
    } else if (direction === 'down' && index < sections.length - 1) {
      [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
    }
    setSections(newSections);
  };

  const saveSection = async (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section) return;
    
    setSaving(true);
    try {
      const contentHtml = collectContent();
      const response = await fetch(buildApiUrl(`/api/newsletters/${id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ contentHtml }),
      });
      if (!response.ok) throw new Error('Error al guardar sección');
      notify({
        title: 'Sección guardada',
        message: 'La sección se guardó correctamente',
        variant: 'success',
      });
    } catch (error) {
      notify({
        title: 'Error',
        message: 'No se pudo guardar la sección',
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!hasEditarNewsletter) {
    return (
      <div className="admin-dashboard">
        <div className="admin-layout">
          <AdminSidebar activeKey="newsletters" onNavigate={(item) => item.path && navigate(item.path)} />
          <main className="admin-main">
            <div className="ANLD-container">
              <Card className="m-4">
                <Card.Body className="text-center py-5">
                  <h5>No tienes permiso para editar newsletters</h5>
                </Card.Body>
              </Card>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-layout">
          <AdminSidebar activeKey="newsletters" onNavigate={(item) => item.path && navigate(item.path)} />
          <main className="admin-main">
            <div className="ANLD-container">
              <Card className="m-4">
                <Card.Body className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3 text-muted">Cargando newsletter...</p>
                </Card.Body>
              </Card>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!newsletter) {
    return (
      <div className="admin-dashboard">
        <div className="admin-layout">
          <AdminSidebar activeKey="newsletters" onNavigate={(item) => item.path && navigate(item.path)} />
          <main className="admin-main">
            <div className="ANLD-container">
              <Card className="m-4">
                <Card.Body className="text-center py-5">
                  <h5>Newsletter no encontrado</h5>
                </Card.Body>
              </Card>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-layout">
        <AdminSidebar
          activeKey="newsletters"
          onNavigate={(item) => {
            if (item.path) {
              navigate(item.path);
              return;
            }
            notify({
              title: 'Sección en desarrollo',
              message: 'Estamos migrando esta sección al nuevo panel.',
              variant: 'info',
            });
          }}
        />
        <main className="admin-main">
          <div className="ANLD-container">
          <section className="nld-cover">
            <div className="nld-cover-image">
              {coverImage ? (
                <img src={resolveImageUrl(coverImage)} alt="Portada" />
              ) : (
                <div className="nld-cover-placeholder">
                  <i className="bi-envelope-paper" />
                </div>
              )}
            </div>
            <div className="nld-actions">
              <Button
                variant="warning"
                onClick={() => coverInputRef.current?.click()}
                disabled={coverUploading}
              >
                {coverUploading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Subiendo...
                  </>
                ) : (
                  <>
                    <i className="bi-image me-2" />
                    Cambiar Portada
                  </>
                )}
              </Button>
              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleCoverUpload}
              />
            </div>
          </section>

          <section className="nld-title-section">
            {titleEditing ? (
              <>
                <Form.Control
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={120}
                  className="nld-title-input"
                />
                <Button
                  variant="success"
                  className="btn-icon"
                  onClick={handleSaveTitle}
                >
                  <i className="bi-check" />
                </Button>
                <Button
                  variant="secondary"
                  className="btn-icon"
                  onClick={() => {
                    setTitle(newsletter.title || '');
                    setTitleEditing(false);
                  }}
                >
                  <i className="bi-x" />
                </Button>
              </>
            ) : (
              <>
                <h1 className="nld-title">{title || 'Sin título'}</h1>
                <Button
                  variant="secondary"
                  className="btn-icon"
                  onClick={() => setTitleEditing(true)}
                >
                  <i className="bi-pencil" />
                </Button>
              </>
            )}
          </section>

          <section className="nld-desc-section">
            <Form.Label>Descripción breve (máx. 250)</Form.Label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Form.Control
                type="text"
                value={shortDescription}
                onChange={(e) => {
                  let v = e.target.value;
                  if (v.length > 250) v = v.substring(0, 250);
                  setShortDescription(v);
                }}
                maxLength={250}
                className="nld-title-input"
                placeholder="Resumen atractivo para mostrar en tarjetas"
              />
              <Button variant="primary" onClick={handleSaveDescription}>
                <i className="bi-save me-2" />
                Guardar
              </Button>
            </div>
            <small style={{ color: '#6b7280' }}>{shortDescription.length}/250</small>
          </section>

          <section className="nld-sections">
            <div className="nld-sections-header">
              <h3 className="nld-sections-title">Secciones del Newsletter</h3>
              <div className="nld-sections-actions">
                <Button variant="primary" onClick={addTextSection}>
                    <i className="bi-plus-circle me-2" />
                    Agregar Sección
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={imageUploading}
                >
                  {imageUploading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Subiendo...
                    </>
                  ) : (
                    <>
                      <i className="bi-image me-2" />
                      Agregar Imagen
                    </>
                  )}
                </Button>
                <Button variant="secondary" onClick={addCTASection}>
                    <i className="bi-bullhorn me-2" />
                    Agregar CTA
                </Button>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleImageUpload}
                />
              </div>
            </div>
            <div className="nld-sections-list">
              {sections.map((section, index) => (
                <div key={section.id} className="nld-section-card">
                  <div className="nld-section-toolbar">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => moveSection(section.id, 'up')}
                      disabled={index === 0}
                    >
                      ↑
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => moveSection(section.id, 'down')}
                      disabled={index === sections.length - 1}
                    >
                      ↓
                    </Button>
                    {section.type === 'text' && (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => saveSection(section.id)}
                        disabled={saving}
                      >
                        {saving ? (
                          <>
                            <Spinner size="sm" className="me-1" />
                            Guardando...
                          </>
                        ) : (
                          'Guardar'
                        )}
                      </Button>
                    )}
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => deleteSection(section.id)}
                    >
                      Eliminar
                    </Button>
                  </div>
                  {section.type === 'image' ? (
                    <img
                      src={section.content}
                      alt="Imagen"
                      className="nld-section-image"
                    />
                  ) : (
                    <>
                      <div
                        className="nld-section-display"
                        dir="ltr"
                        style={{ display: section.editing ? 'none' : 'block' }}
                        onClick={() => {
                          setSections(sections.map(s =>
                            s.id === section.id ? { ...s, editing: true } : s
                          ));
                          // Asegurar dirección LTR al activar edición
                          setTimeout(() => {
                            const editor = editorRefs.current[section.id];
                            if (editor) {
                              editor.setAttribute('dir', 'ltr');
                              editor.style.direction = 'ltr';
                              editor.style.textAlign = 'left';
                              editor.focus();
                            }
                          }, 50);
                        }}
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(section.content) || 'Escribe aquí...'
                        }}
                      />
                      <div
                        ref={(el) => {
                          if (el) {
                            editorRefs.current[section.id] = el;
                            // Solo establecer contenido si no está siendo editado y es diferente
                            if (!isUserEditingRef.current[section.id]) {
                              const currentContent = el.innerHTML.trim();
                              const newContent = section.content || '';
                              if (currentContent !== newContent) {
                                // Guardar posición del cursor
                                const selection = window.getSelection();
                                let cursorPosition = 0;
                                if (selection.rangeCount > 0 && el.contains(selection.anchorNode)) {
                                  const range = selection.getRangeAt(0);
                                  const preCaretRange = range.cloneRange();
                                  preCaretRange.selectNodeContents(el);
                                  preCaretRange.setEnd(range.endContainer, range.endOffset);
                                  cursorPosition = preCaretRange.toString().length;
                                }
                                
                                el.innerHTML = newContent;
                                
                                // Restaurar posición del cursor si es posible
                                if (cursorPosition > 0 && el.firstChild) {
                                  try {
                                    const range = document.createRange();
                                    const walker = document.createTreeWalker(
                                      el,
                                      NodeFilter.SHOW_TEXT,
                                      null
                                    );
                                    let charCount = 0;
                                    let textNode = null;
                                    let offset = 0;
                                    
                                    while (walker.nextNode()) {
                                      const node = walker.currentNode;
                                      const nodeLength = node.textContent.length;
                                      if (charCount + nodeLength >= cursorPosition) {
                                        textNode = node;
                                        offset = cursorPosition - charCount;
                                        break;
                                      }
                                      charCount += nodeLength;
                                    }
                                    
                                    if (textNode) {
                                      range.setStart(textNode, Math.min(offset, textNode.textContent.length));
                                      range.collapse(true);
                                      selection.removeAllRanges();
                                      selection.addRange(range);
                                    }
                                  } catch (err) {
                                    // Ignorar errores de selección
                                  }
                                }
                              }
                            }
                            // Forzar dirección LTR siempre
                            el.setAttribute('dir', 'ltr');
                            el.style.direction = 'ltr';
                            el.style.textAlign = 'left';
                            el.style.unicodeBidi = 'embed';
                          }
                        }}
                        className="nld-section-editor"
                        contentEditable
                        suppressContentEditableWarning
                        dir="ltr"
                        style={{ 
                          display: section.editing ? 'block' : 'none',
                          direction: 'ltr',
                          textAlign: 'left',
                          unicodeBidi: 'embed'
                        }}
                        onInput={(e) => {
                          handleEditorInput(section.id, e);
                        }}
                        onPaste={(e) => {
                          e.preventDefault();
                          const text = e.clipboardData.getData('text/plain');
                          const editor = editorRefs.current[section.id];
                          if (editor) {
                            const selection = window.getSelection();
                            if (selection.rangeCount > 0) {
                              const range = selection.getRangeAt(0);
                              range.deleteContents();
                              const textNode = document.createTextNode(text);
                              range.insertNode(textNode);
                              range.setStartAfter(textNode);
                              range.collapse(true);
                              selection.removeAllRanges();
                              selection.addRange(range);
                              handleEditorInput(section.id, e);
                            }
                          }
                        }}
                        onKeyDown={(e) => {
                          const editor = editorRefs.current[section.id];
                          if (editor) {
                            // Forzar dirección LTR en cada tecla
                            editor.setAttribute('dir', 'ltr');
                            editor.style.direction = 'ltr';
                            editor.style.textAlign = 'left';
                            editor.style.unicodeBidi = 'embed';
                            
                            // Prevenir caracteres RTL
                            if (e.key.length === 1) {
                              const char = e.key;
                              // Si es un carácter que podría causar RTL, forzar LTR
                              if (/[\u0590-\u05FF\u0600-\u06FF\u0700-\u074F\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(char)) {
                                // Estos son rangos de caracteres RTL, pero los permitimos
                                // Solo aseguramos que el contexto sea LTR
                                editor.setAttribute('dir', 'ltr');
                              }
                            }
                          }
                        }}
                        onFocus={() => {
                          handleEditorFocus(section.id);
                        }}
                        onBlur={() => {
                          setSections(prevSections => prevSections.map(s =>
                            s.id === section.id ? { ...s, editing: false } : s
                          ));
                        }}
                      />
                    </>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section className="nld-preview">
            <h3 className="nld-preview-title">Previsualización</h3>
            <iframe
              ref={previewIframeRef}
              className="nld-preview-frame"
              title="Preview"
            />
          </section>
          </div>
        </main>
      </div>
    </div>
  );
};

const AdminNewsletterDetallePage = () => {
  return (
    <AdminNotificationsProvider>
      <AdminNewsletterDetalleInner />
    </AdminNotificationsProvider>
  );
};

export default AdminNewsletterDetallePage;

